import { useCallback, useEffect, useRef, useState } from 'react'

import axios from '../../dataClient/dataClient'
import { openPdf } from '../../util/redaction/pdfjs'
import { annotationToBox, isRedactionAnnotation } from '../../util/redaction/annotationMath'

// Download and parse a PDF, optionally reading its redaction annotations.
export default function usePdfDocument(fileId, { readAnnotations = false } = {}) {
  const [state, setState] = useState({ doc: null, pages: [], boxes: [], loading: true, error: null, version: 0 })
  const docRef = useRef(null)
  const loadSeq = useRef(0)

  const load = useCallback(async () => {
    if (!fileId) return
    const seq = ++loadSeq.current
    setState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const response = await axios.get(`/file/${fileId}/download`, { responseType: 'arraybuffer' })
      if (seq !== loadSeq.current) return
      const doc = await openPdf(response.data)
      if (seq !== loadSeq.current) {
        doc.destroy?.()
        return
      }
      const pages = []
      const boxes = []
      for (let n = 1; n <= doc.numPages; n += 1) {
        const page = await doc.getPage(n)
        pages.push(page)
        if (readAnnotations) {
          const viewport = page.getViewport({ scale: 1 })
          const annots = await page.getAnnotations()
          for (const annot of annots) {
            if (isRedactionAnnotation(annot)) boxes.push(annotationToBox(annot, viewport, n - 1))
          }
        }
      }
      if (seq !== loadSeq.current) {
        doc.destroy?.()
        return
      }
      docRef.current?.destroy?.()
      docRef.current = doc
      setState(prev => ({ doc, pages, boxes, loading: false, error: null, version: prev.version + 1 }))
    } catch (e) {
      if (seq !== loadSeq.current) return
      console.error('usePdfDocument: failed to load PDF', fileId, e)
      setState(prev => ({ ...prev, loading: false, error: e }))
    }
  }, [fileId, readAnnotations])

  useEffect(() => {
    load()
    return () => {
      loadSeq.current += 1
      docRef.current?.destroy?.()
      docRef.current = null
    }
  }, [load])

  return { ...state, reload: load }
}
